import { EVENT_SITES } from '@crossbeam/itly';

import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import useIteratively from '@/composables/useIteratively';
import { useFlashesStore, usePopulationsStore } from '@/stores';
import { Feed } from '@/types/feeds';
import {
  OtherPopulationType,
  Population,
  PopulationPatchPayload,
  PopulationPostPayload,
  PopulationStats,
  PostPopulationResponse,
  StandardPopulationType,
} from '@/types/populations';
import { Source } from '@/types/sources';

// Type assertion to match the working JavaScript pattern
type ApiCallParams =
  | Parameters<typeof crossbeamApi.POST>[1]
  | Parameters<typeof crossbeamApi.PATCH>[1];

export function usePopulationManagement() {
  const flashesStore = useFlashesStore();
  const populationsStore = usePopulationsStore();

  const showConfirmModal = ref(false);
  const populationPreviewCount = ref<number>(0);
  const popCountLoaded = ref(false);

  const { iteratively } = useIteratively();

  function createPreviewPopulation(
    population:
      | (Population & {
          meta?: PopulationStats;
          isEmpty?: boolean;
          tag?: string;
        })
      | null,
    selectedFeed: Feed | null,
    selectedObject: number | null,
    sourceFieldOptions: Source[],
  ) {
    if (population && !population.isEmpty) {
      return population;
    }

    if (selectedFeed && selectedObject) {
      const selectedSource = sourceFieldOptions.find(
        (source) => source.id === selectedObject,
      );

      return {
        ...population,
        source_id: selectedObject,
        base_schema: selectedFeed.schema_name,
        base_table: selectedSource?.table || '',
      };
    }

    return null;
  }

  function handlePreviewCount(count: number) {
    populationPreviewCount.value = count;
    popCountLoaded.value = true;
  }

  function resetPreviewState() {
    populationPreviewCount.value = 0;
    popCountLoaded.value = false;
  }

  async function savePopulation(
    isEdit: boolean,
    population:
      | (Population & {
          meta?: PopulationStats;
          isEmpty?: boolean;
          tag?: string;
        })
      | null,
    selectedFeedId: number | null,
    selectedObject: number | null,
    isCustomPopulation: boolean,
    populationName: string | undefined,
    populationDescription: string | null,
    selectedPopType: StandardPopulationType | OtherPopulationType | null,
    standardType: StandardPopulationType | null,
    sourceFilterExpressions: Record<string, unknown>,
    capitalizedPopulation: string,
    offlinePartnerUuid?: string | null,
  ): Promise<Population | undefined> {
    if (
      !selectedFeedId ||
      !selectedObject ||
      (isCustomPopulation && !populationName)
    ) {
      return;
    }

    if (isEdit) {
      if (!population) return;

      const { error } = await crossbeamApi.PATCH('/v0.3/populations/{id}', {
        params: {
          path: {
            id: population.id,
          },
          ...(offlinePartnerUuid && {
            query: { offline_partner_org_uuid: offlinePartnerUuid },
          }),
        },
        body: {
          source_filter_expressions: sourceFilterExpressions,
          name: isCustomPopulation
            ? populationName || ''
            : capitalizedPopulation,
          description: populationDescription || null,
          standard_type: standardType,
          segment: selectedPopType,
        } as PopulationPatchPayload,
      } as ApiCallParams);

      if (error) {
        const errorMessage =
          (error as { errors: string[] })?.errors?.[0] ||
          'Something went wrong';
        flashesStore.addErrorFlash({ message: errorMessage });
        throw error;
      }
    } else {
      const { data, error } = await crossbeamApi.POST('/v0.3/populations', {
        ...(offlinePartnerUuid && {
          params: { query: { offline_partner_org_uuid: offlinePartnerUuid } },
        }),
        body: {
          standard_type: standardType,
          source_filter_expressions: sourceFilterExpressions,
          source_id: selectedObject,
          name: isCustomPopulation
            ? populationName || ''
            : capitalizedPopulation,
          segment: selectedPopType,
          description: populationDescription || null,
        } as PopulationPostPayload,
      } as ApiCallParams);

      if (error) {
        const errorMessage =
          (error as { errors: string[] })?.errors?.[0] ||
          'Something went wrong';
        flashesStore.addErrorFlash({ message: errorMessage });
        throw error;
      }

      if (data) {
        iteratively.userCreatedPopulation({
          population_name: (data as PostPopulationResponse)?.name || '',
          population_id: (data as PostPopulationResponse)?.id.toString() || '',
          event_site: EVENT_SITES.POPULATION_MODAL,
        });
        await populationsStore.refreshPopulationsStore();
        showConfirmModal.value = false;
        resetPreviewState();
        return data as Population;
      }
    }

    if (isEdit) {
      await populationsStore.refreshPopulationsStore();
      showConfirmModal.value = false;
      resetPreviewState();
    }

    return undefined;
  }

  return {
    showConfirmModal,
    populationPreviewCount,
    popCountLoaded,
    createPreviewPopulation,
    handlePreviewCount,
    resetPreviewState,
    savePopulation,
  };
}
